import {
    createContext,
    FC,
    useCallback,
    useEffect,
    useReducer,
    useState,
} from 'react'
import {
    GlobalState,
    initialGlobalState,
    globalReducer,
    Levels,
} from '../store/Global'
import {
    EndeavorAuthType,
    UserDetailsTypes,
    UserEndeavorPreferences,
} from '../../utils/types/auth'
import { SocialLink } from '../../utils/types/ContentStack'
import { LicencesType } from '../../utils/types/common'
import { AUTHENTICATED_USER, LICENCE_PLAN } from '../../utils/constants/enum'
import { useErrorHandlingSnackbar } from '../../utils/custom-hooks/useErrorHandlingSnackbar'
import { userLicence } from '../../utils/constants'
import { getUserEndeavorPreferences } from '../../services/endeavor'
import { BucketItem } from '../../utils/types/Endeavor'
import { useSentryError } from '../../utils/custom-hooks/useSentryError'

interface GlobalContextInterface extends GlobalState {
    userSubscribedPlan: LicencesType | null
    userPreference: UserEndeavorPreferences
    isMobileMenuOpen: boolean
    onFetchUserLicence: () => void
    getUserPreference: () => Promise<void>
    setEndeavorToken: (endeavorToken: EndeavorAuthType) => void
    setUserData: (userData: UserDetailsTypes | null) => void
    setGuestUserRegModal: () => void
    setMobileMenuOpen: (isOpen: boolean) => void
    setSocialLinks: (socialLinks: SocialLink) => void
    setIsUserAddingCard: (isAddingCard: boolean) => void
    hidePromoBanner: () => void
    setWeeklySchedule: (level: Levels, data: Array<BucketItem>) => void
    setActiveLevel: (activeLevel: Levels) => void
}

/**
 * Global Context
 * this holds the values that are needed throughout the project
 * or require some sort of long polling strategies
 * like keeping track of current live and notifications
 */
export const GlobalContext = createContext<GlobalContextInterface>({
    ...initialGlobalState,
    userSubscribedPlan: null,
    isMobileMenuOpen: false,
    userPreference: { profileImgUrl: '' },
    setEndeavorToken: () => {
        // This is intentional
    },
    getUserPreference: async () => {
        // This is intentional
    },
    setUserData: () => {
        // This is intentional
    },
    setGuestUserRegModal: () => {
        // This is intentional
    },
    setMobileMenuOpen: () => {
        // This is intentional
    },
    setSocialLinks: () => {
        // This is intentional
    },
    onFetchUserLicence: () => {
        /* This is intentional*/
    },
    setIsUserAddingCard: () => {
        /* This is intentional*/
    },
    hidePromoBanner: () => {
        /* This is intentional*/
    },
    setWeeklySchedule: () => {
        /* This is intentional*/
    },
    setActiveLevel: () => {
        /* This is intentional*/
    },
})

/**
 * Global Provider
 * the is the provider for the global context
 */
export const GlobalProvider: FC = ({ children }) => {
    const [state, dispatch] = useReducer(globalReducer, initialGlobalState)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false)
    const { SnackBarDOM, setSnackBarMessage } = useErrorHandlingSnackbar()
    const { handleError } = useSentryError()
    const [userSubscribedPlan, setUserSubscribedPlan] =
        useState<LicencesType | null>(null)
    const [isUserAddingCard, setUserAddingCard] = useState<boolean>(false)
    const [userPreference, setUserPreference] =
        useState<UserEndeavorPreferences>({ profileImgUrl: '' })

    const setEndeavorToken = useCallback(
        (token) => {
            dispatch({
                type: 'SET_ENDEAVOR_TOKEN',
                payload: token,
            })
            if (!token.authorisationToken) {
                setUserSubscribedPlan(null)
            }
        },
        [dispatch]
    )

    const setUserData = useCallback(
        (user) => {
            dispatch({
                type: 'SET_USER_DATA',
                payload: user,
            })
        },
        [dispatch]
    )

    const setSocialLinks = useCallback(
        (socialLinks) => {
            dispatch({ type: 'SET_SOCIAL_LINKS', payload: socialLinks })
        },
        [dispatch]
    )

    useEffect(() => {
        setEndeavorToken(state.endeavorToken)
    }, [state.endeavorToken, setEndeavorToken])

    const getUserSubscribedPlan = useCallback(async () => {
        try {
            const isAuthUser = localStorage.getItem(
                AUTHENTICATED_USER.IS_LOGIN_USER
            )
            if (isAuthUser) {
                const userActiveLicence = await userLicence().catch((error) =>
                    console.log(error)
                )
                if (userActiveLicence) {
                    const { currentActiveLicence, activeLicence } =
                        userActiveLicence
                    localStorage.removeItem(LICENCE_PLAN.IS_UNDER_PROCESS)
                    if (currentActiveLicence) {
                        setUserSubscribedPlan(currentActiveLicence)
                    }
                    if (activeLicence?.[0]) {
                        setUserSubscribedPlan(activeLicence[0])
                    }
                }
            }
        } catch (error) {
            handleError(error, 'get user subscribed plan')
            setSnackBarMessage(error)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const setGuestUserRegModal = useCallback(() => {
        dispatch({
            type: 'SET_GUEST_USER_REGISTRATION_MODAL',
            payload: !isModalOpen,
        })
        setIsModalOpen(!isModalOpen)
    }, [dispatch, isModalOpen])

    const setMobileMenuOpen = (isMenuOpen: boolean) => {
        setIsMobileMenuOpen(isMenuOpen)
    }

    const onFetchUserLicence = () => {
        getUserSubscribedPlan().catch((error) => console.log(error))
    }

    const setIsUserAddingCard = (isAddingCard: boolean) => {
        setUserAddingCard(isAddingCard)
    }

    const getUserPreference = useCallback(async () => {
        try {
            const isAuthUser = localStorage.getItem(
                AUTHENTICATED_USER.IS_LOGIN_USER
            )
            if (isAuthUser === AUTHENTICATED_USER.IS_AUTHENTICATED_USER) {
                const userPref = await getUserEndeavorPreferences()
                if (userPref) {
                    setUserPreference(userPref)
                }
            }
        } catch (error) {
            handleError(error, 'get user preference')
            setSnackBarMessage(error)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        getUserPreference().catch((error) => console.log('error', error))
    }, [getUserPreference])

    useEffect(() => {
        getUserSubscribedPlan().catch((error) => console.log(error))
    }, [getUserSubscribedPlan])

    const hidePromoBanner = () => {
        dispatch({
            type: 'TOGGLE_PROMOTION',
            payload: false,
        })
    }

    const setWeeklySchedule = (level: Levels, data: Array<BucketItem>) => {
        dispatch({
            type: 'SET_WEEKLY_SCHEDULE',
            payload: {
                level,
                data,
            },
        })
    }

    const setActiveLevel = (activeLevel: Levels) => {
        dispatch({
            type: 'SET_ACTIVE_LEVEL',
            payload: activeLevel,
        })
    }

    return (
        <>
            <GlobalContext.Provider
                value={{
                    ...state,
                    userPreference,
                    isUserAddingCard,
                    userSubscribedPlan,
                    isMobileMenuOpen,
                    onFetchUserLicence,
                    getUserPreference,
                    setEndeavorToken,
                    setUserData,
                    setGuestUserRegModal,
                    setMobileMenuOpen,
                    setSocialLinks,
                    setIsUserAddingCard,
                    hidePromoBanner,
                    setWeeklySchedule,
                    setActiveLevel,
                }}
            >
                {SnackBarDOM}
                {children}
            </GlobalContext.Provider>
        </>
    )
}
